import { useState, useEffect } from 'react';
import {
  subscribeToAgentRegistry,
  subscribeToAgentAlerts,
  subscribeToAgentEvents,
  subscribeToAgentActions,
  markAlertAsRead,
  markEventResolved,
  toggleAgent,
  AGENT_TAB_MAPPING,
} from '../../../services/agentService';

// ============================================================
// Helpers
// ============================================================

function timeAgo(timestamp) {
  if (!timestamp) return 'never';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const SEVERITY_COLORS = {
  critical: { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', badge: 'bg-red-100 text-red-800' },
  warning: { border: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800' },
  info: { border: 'border-gray-300', bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-700' },
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  error: 'bg-red-100 text-red-800',
  not_implemented: 'bg-gray-100 text-gray-500',
};

const TYPE_COLORS = {
  security: 'bg-red-50 text-red-700 border border-red-200',
  payment: 'bg-green-50 text-green-700 border border-green-200',
  monitoring: 'bg-blue-50 text-blue-700 border border-blue-200',
  marketing: 'bg-purple-50 text-purple-700 border border-purple-200',
  onboarding: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  prospecting: 'bg-orange-50 text-orange-700 border border-orange-200',
  qa: 'bg-teal-50 text-teal-700 border border-teal-200',
  enrichment: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
};

// ============================================================
// AgentsTab
// ============================================================

export default function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [drilldownActions, setDrilldownActions] = useState([]);
  const [expandedEvent, setExpandedEvent] = useState(null);

  // Filters
  const [eventAgentFilter, setEventAgentFilter] = useState('all');
  const [eventSeverityFilter, setEventSeverityFilter] = useState('all');
  const [eventTimeFilter, setEventTimeFilter] = useState('7d');

  useEffect(() => {
    const unsubs = [];
    unsubs.push(subscribeToAgentRegistry(setAgents));
    unsubs.push(subscribeToAgentAlerts(setAlerts, { unreadOnly: false, limit: 50 }));

    // Time filter
    let since = null;
    if (eventTimeFilter === '24h') since = new Date(Date.now() - 24 * 60 * 60 * 1000);
    else if (eventTimeFilter === '7d') since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    else if (eventTimeFilter === '30d') since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

    unsubs.push(subscribeToAgentEvents(setEvents, {
      agentId: eventAgentFilter === 'all' ? null : eventAgentFilter,
      severity: eventSeverityFilter === 'all' ? null : eventSeverityFilter,
      since,
      limit: 100,
    }));

    return () => unsubs.forEach((u) => u());
  }, [eventAgentFilter, eventSeverityFilter, eventTimeFilter]);

  // Drilldown: load actions when an agent is selected
  useEffect(() => {
    if (!selectedAgent) return;
    const unsub = subscribeToAgentActions(setDrilldownActions, { agentId: selectedAgent.id, limit: 10 });
    return unsub;
  }, [selectedAgent]);

  const unreadAlerts = alerts.filter((a) => !a.read);
  const sortedAlerts = [...alerts].sort((a, b) => {
    // Critical first
    if (a.severity === 'critical' && b.severity !== 'critical') return -1;
    if (b.severity === 'critical' && a.severity !== 'critical') return 1;
    // Then unread first
    if (!a.read && b.read) return -1;
    if (a.read && !b.read) return 1;
    return 0;
  });

  return (
    <div className="space-y-8">
      {/* SECTION 1: Agent Registry Cards */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Agent Registry</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.sort((a, b) => (a.status === 'active' ? -1 : 1)).map((agent) => (
            <div key={agent.id} className="bg-white shadow rounded-lg p-5 border border-gray-200">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{agent.name}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[agent.status] || STATUS_COLORS.not_implemented}`}>
                  {agent.status}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[agent.type] || 'bg-gray-100 text-gray-600'}`}>
                  {agent.type}
                </span>
                {agent.version && agent.version !== '0.0.0' && (
                  <span className="text-xs text-gray-400">v{agent.version}</span>
                )}
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{agent.description}</p>

              {/* Manages line */}
              {AGENT_TAB_MAPPING[agent.id] && AGENT_TAB_MAPPING[agent.id].length > 0 && (
                <p className="text-xs text-gray-400 mb-3">
                  Manages: {AGENT_TAB_MAPPING[agent.id].join(', ')}
                </p>
              )}

              {/* Counts */}
              {agent.status !== 'not_implemented' && (
                <div className="flex items-center gap-3 mb-3 text-xs">
                  {agent.criticalCount7d > 0 && (
                    <span className="text-red-600 font-semibold">{agent.criticalCount7d} critical</span>
                  )}
                  {agent.warningCount7d > 0 && (
                    <span className="text-yellow-600 font-semibold">{agent.warningCount7d} warning</span>
                  )}
                  {agent.infoCount7d > 0 && (
                    <span className="text-gray-500">{agent.infoCount7d} info</span>
                  )}
                  <span className="text-gray-400">({agent.totalEventsAllTime || 0} total)</span>
                </div>
              )}

              {/* Last event */}
              {agent.lastEventAt && (
                <p className="text-xs text-gray-400 mb-3">Last event: {timeAgo(agent.lastEventAt)}</p>
              )}

              {/* Controls */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                {agent.status !== 'not_implemented' ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agent.enabled !== false}
                      onChange={(e) => toggleAgent(agent.id, e.target.checked)}
                      className="rounded border-gray-300 text-apn-blue focus:ring-apn-blue"
                    />
                    <span className="text-xs text-gray-500">Enabled</span>
                  </label>
                ) : (
                  <span className="text-xs text-gray-400 italic">Not yet built</span>
                )}
                <button
                  onClick={() => setSelectedAgent(agent)}
                  className="text-xs text-apn-blue hover:text-apn-dark-blue font-medium"
                >
                  View details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 2: Live Alert Feed */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Alerts</h2>
          {unreadAlerts.length > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full h-5 min-w-[1.25rem] px-1.5 flex items-center justify-center">
              {unreadAlerts.length}
            </span>
          )}
        </div>

        {sortedAlerts.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center text-gray-400">
            No alerts yet. Alerts will appear here when agents detect issues.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {sortedAlerts.map((alert) => {
              const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
              return (
                <div key={alert.id} className={`bg-white shadow rounded-lg p-4 border-l-4 ${colors.border} ${alert.read ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colors.badge}`}>{alert.severity}</span>
                        <span className="text-xs text-gray-400">{alert.agentId}</span>
                        <span className="text-xs text-gray-400">{timeAgo(alert.createdAt)}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900">{alert.title}</p>
                      <p className="text-xs text-gray-600 mt-1">{alert.summary}</p>
                    </div>
                    {!alert.read && (
                      <button
                        onClick={() => markAlertAsRead(alert.id)}
                        className="ml-3 text-xs text-apn-blue hover:text-apn-dark-blue whitespace-nowrap"
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SECTION 3: Recent Events Timeline */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Events</h2>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={eventAgentFilter}
            onChange={(e) => setEventAgentFilter(e.target.value)}
            className="rounded-md border-gray-300 text-sm shadow-sm focus:border-apn-blue focus:ring-apn-blue"
          >
            <option value="all">All Agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>

          <select
            value={eventSeverityFilter}
            onChange={(e) => setEventSeverityFilter(e.target.value)}
            className="rounded-md border-gray-300 text-sm shadow-sm focus:border-apn-blue focus:ring-apn-blue"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>

          <select
            value={eventTimeFilter}
            onChange={(e) => setEventTimeFilter(e.target.value)}
            className="rounded-md border-gray-300 text-sm shadow-sm focus:border-apn-blue focus:ring-apn-blue"
          >
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>

        {events.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center text-gray-400">
            No events match the current filters.
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sev</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Summary</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((evt) => {
                  const colors = SEVERITY_COLORS[evt.severity] || SEVERITY_COLORS.info;
                  return (
                    <tr key={evt.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedEvent(expandedEvent === evt.id ? null : evt.id)}>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{timeAgo(evt.timestamp)}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{evt.agentId}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block w-2 h-2 rounded-full ${colors.dot}`}></span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{evt.title}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate">{evt.summary}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Expanded event details */}
            {expandedEvent && (() => {
              const evt = events.find((e) => e.id === expandedEvent);
              if (!evt) return null;
              return (
                <div className="bg-gray-50 border-t border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">Event Details</h4>
                    {!evt.resolved && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markEventResolved(evt.id); }}
                        className="text-xs text-apn-blue hover:text-apn-dark-blue"
                      >
                        Mark resolved
                      </button>
                    )}
                  </div>
                  <pre className="text-xs text-gray-600 bg-white rounded p-3 overflow-auto max-h-48 border">
                    {JSON.stringify(evt.details || {}, null, 2)}
                  </pre>
                  {evt.targetCollection && (
                    <p className="text-xs text-gray-400 mt-2">Target: {evt.targetCollection}/{evt.targetDocId}</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* SECTION 4: Per-Agent Drilldown Modal */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedAgent(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedAgent.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[selectedAgent.type] || 'bg-gray-100'}`}>
                      {selectedAgent.type}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedAgent.status]}`}>
                      {selectedAgent.status}
                    </span>
                    {selectedAgent.version && <span className="text-xs text-gray-400">v{selectedAgent.version}</span>}
                  </div>
                </div>
                <button onClick={() => setSelectedAgent(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <p className="text-sm text-gray-600 mb-4">{selectedAgent.description}</p>

              {AGENT_TAB_MAPPING[selectedAgent.id] && AGENT_TAB_MAPPING[selectedAgent.id].length > 0 && (
                <p className="text-sm text-gray-500 mb-4">
                  <span className="font-medium">Manages:</span> {AGENT_TAB_MAPPING[selectedAgent.id].join(', ')}
                </p>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{selectedAgent.totalEventsAllTime || 0}</p>
                  <p className="text-xs text-gray-500">Total Events</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{selectedAgent.eventCount7d || 0}</p>
                  <p className="text-xs text-gray-500">Last 7 Days</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{selectedAgent.criticalCount7d || 0}</p>
                  <p className="text-xs text-gray-500">Critical (7d)</p>
                </div>
              </div>

              {selectedAgent.lastEventAt && (
                <p className="text-xs text-gray-400 mb-4">Last event: {timeAgo(selectedAgent.lastEventAt)}</p>
              )}

              {/* Toggle */}
              {selectedAgent.status !== 'not_implemented' && (
                <div className="flex items-center gap-3 mb-6">
                  <button
                    onClick={() => toggleAgent(selectedAgent.id, !selectedAgent.enabled)}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      selectedAgent.enabled ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200'
                    }`}
                  >
                    {selectedAgent.enabled ? 'Pause Agent' : 'Resume Agent'}
                  </button>
                  {/* TODO: "Run smoke test" button for Security Agent */}
                </div>
              )}

              {/* Recent events for this agent */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent Events</h4>
                {events.filter((e) => e.agentId === selectedAgent.id).length === 0 ? (
                  <p className="text-xs text-gray-400">No events yet</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {events.filter((e) => e.agentId === selectedAgent.id).slice(0, 20).map((evt) => {
                      const colors = SEVERITY_COLORS[evt.severity] || SEVERITY_COLORS.info;
                      return (
                        <div key={evt.id} className={`text-xs p-2 rounded ${colors.bg}`}>
                          <span className={`font-medium ${colors.text}`}>[{evt.severity}]</span>{' '}
                          <span className="text-gray-700">{evt.title}</span>{' '}
                          <span className="text-gray-400">{timeAgo(evt.timestamp)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Recent actions */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent Actions</h4>
                {drilldownActions.length === 0 ? (
                  <p className="text-xs text-gray-400">No actions taken yet</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {drilldownActions.map((action) => (
                      <div key={action.id} className="text-xs p-2 rounded bg-gray-50 border border-gray-100">
                        <span className="font-medium text-gray-700">{action.actionType}</span>{' '}
                        <span className="text-gray-500">on {action.targetCollection}/{action.targetDocId}</span>{' '}
                        <span className={action.success ? 'text-green-600' : 'text-red-600'}>{action.success ? 'OK' : 'FAILED'}</span>{' '}
                        <span className="text-gray-400">{timeAgo(action.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
